"use client";

import { useTranslations } from "use-intl";

import { EntityAboutMore } from "@/components/catalog-entity/entity-about-more";
import type { EntityAboutSection } from "@/components/catalog-entity/entity-about-section";
import {
  ExternalLinkGroups,
  type ExternalLinkGroup,
} from "@/components/catalog-entity/external-link-groups";
import { SOCIAL_PLATFORM_META } from "@/components/social-platform-icon";
import { catalogUrlLabel } from "@/lib/catalog-url-label";
import type { CatalogUrl, SocialLink } from "@garimpa/lib/musicTypes";

const LINK_PREVIEW = 4;

interface LinksInput {
  officialUrls: CatalogUrl[];
  socialUrls: SocialLink[];
  otherUrls: CatalogUrl[];
  sourceLinks?: ReadonlyArray<{ source: string; label: string; url: string }>;
}

/** The shared "Links" About section: reference sites preview, grouped list in the panel. */
export function useLinksAboutSection({
  officialUrls,
  socialUrls,
  otherUrls,
  sourceLinks = [],
}: LinksInput): EntityAboutSection {
  const t = useTranslations("shared.entityAbout");
  const catalogLink = (link: CatalogUrl) => ({
    url: link.url,
    label: catalogUrlLabel(link.url),
    title: link.type,
  });
  const groups: ExternalLinkGroup[] = [
    { key: "official", links: officialUrls.map(catalogLink) },
    {
      key: "social",
      links: socialUrls.map((link) => ({
        url: link.url,
        label: SOCIAL_PLATFORM_META[link.platform].label,
        title: link.platform,
        platform: link.platform,
      })),
    },
    { key: "reference", links: otherUrls.map(catalogLink) },
    {
      key: "sources",
      links: sourceLinks.map((link) => ({ url: link.url, label: link.label, title: link.source })),
    },
  ];
  const count = groups.reduce((total, group) => total + group.links.length, 0);
  const preview = [...groups[2].links, ...groups[3].links, ...groups[0].links].slice(
    0,
    LINK_PREVIEW,
  );

  return {
    key: "links",
    label: t("links"),
    count,
    preview: (
      <>
        {preview.map((link) => (
          <a
            key={link.url}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            title={link.title}
            className="text-foreground/85 hover:text-primary"
          >
            {link.label}
          </a>
        ))}
        <EntityAboutMore count={count - preview.length} />
      </>
    ),
    content: <ExternalLinkGroups groups={groups} />,
  };
}
